import { CommonModule } from '@angular/common';
import { Component, EventEmitter, inject, Output, Input } from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import {
  ButtonsModule,
  CardModule,
  IconModule,
  LoadingModule,
  LoadingService,
} from 'axis-components';
import { Logger } from '../../../_core/logger.service';
import { NotificationService } from '../../../_services/notification/notification.service';
import { PdfService } from '../../../_services/pdf/pdf.service';
import { RequestService } from '../../../_services/request/request.service';
import { AnalyticsService } from '../../../_shared/analytics/analytics.service';
import { FormType, PDFGenerationStatus } from '../../../config/constants';
import { UimuiBaseRulesService } from '../../../state-disclosures/uimui/services/uimui-base-rules.service';
import {
  DefaultNote,
  DigitalContent,
  NonDigitalContent,
  Messages,
  FileNames,
} from './uimui-thankyou.constants';
@Component({
  selector: 'app-uimui-thankyou',
  templateUrl: './uimui-thankyou.component.html',
  styleUrls: ['./uimui-thankyou.component.scss'],
  standalone: true,
  imports: [
    CardModule,
    ButtonsModule,
    IconModule,
    RouterModule,
    LoadingModule,
    CommonModule,
  ],
})
export class UimuiThankYouComponent {
  // Event emitted to playground component when workflow completes
  @Output() workflowCompletePlayground = new EventEmitter<void>();
  @Input() ctaData: any = {};

  title;
  content = '';
  subcontent;
  showDocument = false;

  downloadInProgress = false;
  requestKey = '';

  isPipEnabled = false;

  note = DefaultNote.Note;
  public uimuiBaseRulesService = inject(UimuiBaseRulesService);
  constructor(
    private router: Router,
    public route: ActivatedRoute,
    private pdfService: PdfService,
    private requestService: RequestService,
    private analyticsService: AnalyticsService,
    private notificationService: NotificationService,
    private loadingService: LoadingService,
  ) {}
  ngOnInit(): void {
    this.requestKey = this.requestService.getRequestKey() || '';
    this.requestService.refreshRequestData(true).subscribe({
      next: (data) => {
        this.initializeFromApiData();
      },
      error: (err) => {
        console.error(Messages.ErrorFetchingData, err);
        // Fallback to existing data if API call fails
        this.initializeFromApiData();
      },
    });
    this.goTop();
  }
  private initializeFromApiData(): void {
    let formTypeList = this.requestService.requestFormTypeList;
    this.isPipEnabled = formTypeList.includes(FormType.PIP);
    // Determine content and display settings based on step data
    this.determineContentFromSteps();
  }

  private determineContentFromSteps(): void {
    const uimuiConfig = this.requestService.getThankYouPageConfig('um_uim');

    if (uimuiConfig.isDigital) {
      // Different content based on whether PIP is enabled or not
      if (this.isPipEnabled) {
        this.content = DigitalContent.WithPip;
      } else {
        this.content = DigitalContent.WithoutPip;
      }
      this.showDocument = true;
    } else if (
      uimuiConfig.isNonDigitalMail ||
      uimuiConfig.isNonDigitalDownload
    ) {
      // Different content based on whether PIP is enabled or not for non-digital scenarios
      if (this.isPipEnabled) {
        if (uimuiConfig.isNonDigitalMail) {
          this.content = NonDigitalContent.WithoutPip;
        } else {
          this.content = NonDigitalContent.WithPip;
        }
      } else {
        this.content = NonDigitalContent.WithoutPip;
      }
      this.showDocument = true;
    }
  }

  // Helper methods for template logic
  isUimuiNonDigital(): boolean {
    const uimuiConfig = this.requestService.getThankYouPageConfig('um_uim');
    return uimuiConfig.isNonDigitalDownload || uimuiConfig.isNonDigitalMail;
  }

  downloadUIUMDocuments() {
    this.downloadInProgress = true;
    this.analyticsService.logDownloadButtonClick(FormType.UM_UIM);
    this.getPdfFromGDMS();
  }

  getPdfFromGDMS() {
    this.downloadInProgress = true;
    this.pdfService.getPdfFromGDMS(FormType.UM_UIM, this.requestKey).subscribe({
      next: (response) => {
        this.downloadInProgress = false;
        this.handlePdfResponse(response);
      },
      error: (err) => {
        this.downloadInProgress = false;
        console.error('Error generating PDF:', err);
        this.notificationService.showNotification(
          Messages.DocumentsUnavailableError,
          'error',
        );
      },
    });
  }


  handlePdfResponse(response: any) {
    const blob = new Blob([response], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    // To download:
    const a = document.createElement('a');
    a.href = url;
    a.download = FileNames.UiUimDocuments;
    a.click();
    window.URL.revokeObjectURL(url);
  }

  goTop(): void {
    // reset the page when changing route
    window.scrollTo(0, 0);
  }
  onContinue(): void {
    // Get current origin for dynamic URL construction
    const baseUrl = window.location.origin;
    const targetRoute = '/insured/pip-consent';

    // Setup CTA data for PIP start tracking from thank you page
    this.ctaData.ctaName = 'DigiSign:um_uim:thankYou:continue';
    this.ctaData.ctaUrl = `${baseUrl}${targetRoute}`;
    this.analyticsService.setAdobeDataLayerCTAEvent(this.ctaData);
    this.router.navigate([targetRoute], {
      queryParamsHandling: 'preserve',
    });
  }
}
